'use client'

import {
  navigateWizardRoute,
  useWizardNavigationState,
} from '@/components/_championship/forms/wizard-navigation/store'
import { cn } from '@/lib/utils'
import apiFetch from '@/store/helpers/api-fetch'
import { useRouter } from 'next/navigation'
import { type ComponentProps, useEffect, useState } from 'react'
import useSWRImmutable from 'swr/immutable'
import CommunityDisplay from './community-display'
import MobileDropdownNav from './mobile-dropdown-nav'
import {
  canSkipToSection,
  getSidebarItemStateForChampionship,
  sidebarItems,
} from './sidebar-items/championship-sidebar-items'

type CreateContentProps = {
  params: {
    communityId: string
    championshipId: string
  }
  pathname: string
  session: ComponentProps<typeof CommunityDisplay>['session']
  serverInfoFeatureEnabled?: boolean
}

export default function CreateContent({
  params,
  pathname,
  session,
  serverInfoFeatureEnabled = true,
}: CreateContentProps) {
  const router = useRouter()
  const [storedStep, setStoredStep] = useState<string | null>(null)
  const [sidebarItemState, setSidebarItemState] = useState<
    ReturnType<typeof getSidebarItemStateForChampionship>
  >({})
  const { isNavigating } = useWizardNavigationState()
  const visibleSidebarItems = sidebarItems.filter(
    (item) => serverInfoFeatureEnabled || item.urlSnippet !== 'server-info',
  )

  const { data: community, isLoading } = useSWRImmutable(
    {
      url: `community/${params.communityId}`,
      searchParams: {
        select: {
          id: true,
          name: true,
          logoImgUrl: true,
          isShowcase: true,
          championships: {
            where: {
              deleteTs: null,
            },
            select: {
              id: true,
              name: true,
              headerImgUrl: true,
            },
          },
        },
      },
    },
    apiFetch,
  )

  useEffect(() => {
    setStoredStep(
      window.localStorage.getItem(`championship_${params.championshipId}_form`),
    )
    setSidebarItemState(
      getSidebarItemStateForChampionship(params.championshipId),
    )
  }, [params.championshipId, pathname])

  return (
    <>
      <div className="tablet:hidden">
        <MobileDropdownNav
          options={visibleSidebarItems}
          baseUrl={`/manage/${params.communityId}/racing/create`}
          hasOutstandingApplications={false}
          useWizardNavigation
        />
      </div>

      <div className="flex flex-col max-tablet:hidden">
        <CommunityDisplay
          community={community}
          communityLoading={isLoading}
          session={session}
        />

        <p className="mb-3 mt-5 text-p-1 font-bold text-neutral-blue-400">
          Create
        </p>

        {visibleSidebarItems.map((item, index) => {
          const isOptionalDisabled =
            sidebarItemState[item.urlSnippet]?.optionalDisabled === true
          const isCompleted =
            canSkipToSection(item.urlSnippet, storedStep) ||
            pathname.endsWith('review')
          const isPending = !isCompleted && !isOptionalDisabled
          const canNavigateToItem = isCompleted || isOptionalDisabled

          return (
            <div
              key={item.title}
              className={cn(
                'flex cursor-pointer items-center gap-2 rounded-sm p-2 transition-all',
                {
                  'cursor-default': isPending,
                  'pointer-events-none opacity-60': isNavigating,
                  'text-shades-0 hover:bg-neutral-blue-600':
                    canNavigateToItem && !isOptionalDisabled,
                  'hover:bg-neutral-blue-600':
                    canNavigateToItem && isOptionalDisabled,
                  'text-neutral-blue-300': isPending,
                  'text-neutral-blue-600 opacity-45': isOptionalDisabled,
                },
              )}
              onClick={() => {
                if (canNavigateToItem && !isNavigating) {
                  void navigateWizardRoute({
                    router,
                    href: `/manage/${params.communityId}/racing/create/${params.championshipId}/${item.urlSnippet}`,
                    replace: true,
                  })
                }
              }}
            >
              <div
                className={cn(
                  'flex aspect-square items-center justify-center rounded-sm bg-neutral-blue-700 stroke-shades-0 p-1 *:size-5',
                  {
                    'bg-shades-0 [&>svg]:stroke-neutral-blue-900':
                      pathname.endsWith(item.urlSnippet),
                    'bg-neutral-blue-900 stroke-neutral-blue-600':
                      isOptionalDisabled && !pathname.endsWith(item.urlSnippet),
                    'bg-neutral-blue-700 stroke-neutral-blue-300':
                      isPending && !pathname.endsWith(item.urlSnippet),
                  },
                )}
              >
                {item.icon}
              </div>
              <p className="text-p-1 font-bold">
                {index + 1}. {item.title}
              </p>
            </div>
          )
        })}
      </div>
    </>
  )
}
